import React, { useState, useContext, useEffect } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import UserContext from './UserContext';
import {
  Container, Typography, Box, Tab, Tabs, Button,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  LinearProgress, Paper, TextField, Stack
} from '@mui/material';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import SlowMotionVideoOutlinedIcon from '@mui/icons-material/SlowMotionVideoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CheckroomOutlinedIcon from '@mui/icons-material/CheckroomOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import VideoFileInput from './VideoFileInput';
import useMediaQuery from '@mui/material/useMediaQuery';
import { addDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { toast } from './toast';
import { useNavigate } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import PendingImage from './PendingImage';
import posterImage from './assets/poster.jpg';
import videoAngle from './assets/angle.mp4';
import videoClothes from './assets/clothes.mp4';
import videoFps from './assets/fps.mp4';
import videoLength from './assets/length.mp4';
import videoSetting from './assets/setting.mp4';

const Highlight = ({ children }) => (
  <Box
    component="span"
    sx={{
      textDecoration: 'underline',
      textDecorationStyle: 'dotted',
      textDecorationColor: 'currentColor',
      textUnderlineOffset: '5px',
    }}
  >
    {children}
  </Box>
);


function VerticalTabPanel(props) {
  const { text, videoUrl, value, index, onStepChange, ...other } = props;
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('md'));

  return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box display="flex" flexDirection={isLargeScreen ? 'row' : 'column'} alignItems={isLargeScreen ? 'stretch' : 'center'} width="100%" position="relative">
            <Box flexGrow={1} px={isLargeScreen ? 4 : 1} py={isLargeScreen ? 0 : 1} display="flex" flexDirection="column" alignItems="center">
              <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center" width="100%">
                <Typography align="center" variant={isLargeScreen ? 'h4' : 'subtitle1'} sx={{ fontWeight: 'normal', px: isLargeScreen ? 8 : 0 }}>
                  {text}
                </Typography>
              </Box>
              {isLargeScreen && (
                <Box display="flex" gap={1} pb={3}>
                  {[...Array(5)].map((_, i) => (
                    <Box
                      key={i}
                      onClick={() => onStepChange(i)}
                      sx={{
                        width: i === value ? 26 : 12, height: 12, borderRadius: '6px',
                        bgcolor: i === value ? 'primary.main' : 'action.disabled',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        animation: i === value ? 'dotPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
                        '@keyframes dotPop': { '0%': { transform: 'scale(0.6)' }, '100%': { transform: 'scale(1)' } },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: isLargeScreen ? 'flex-end' : 'center',
                width: isLargeScreen ? 'auto' : '100%',
              }}
            >
              <video
                style={{
                  height: isLargeScreen ? '300px' : 'auto',
                  width: isLargeScreen ? '337.5px' : '100%',
                }}
                autoPlay
                muted
                loop
                playsInline
                controls
                poster={posterImage}
              >
                <source src={videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </Box>
          </Box>
        )}
      </Box>
    );
}

function UploadPage() {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [goal, setGoal] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [videoPending, setVideoPending] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [symptomsText, setSymptomsText] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const navigate = useNavigate();

  const { user, db, storage, analytics } = useContext(UserContext);

  useEffect(() => {
    document.title = "Forma | Upload";

    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const q = query(
          collection(db, 'submissions'),
          where('userId', '==', user.uid),
          where('status', '==', 'pending'));
        const querySnapshot = await getDocs(q);
        setVideoPending(!querySnapshot.empty);
      } catch (err) {
        console.error(err);
        setLoadError(true);
        logEvent(analytics, 'page_load_error', {error: err.message, page: 'upload'});
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user, db, analytics]);

  const handleUpload = async (event) => {
    event.preventDefault();

    if (file && user && goal && !isUploading) {
      setIsUploading(true);
      // Generate a timestamp
      const timestamp = Date.now();

      // Sanitize filename: remove spaces and special characters
      const sanitizedName = file.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._]/g, '');

      // Generate the storage reference
      const storageRef = ref(storage, `videos/${user.uid}/${timestamp}_${sanitizedName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        (snapshot) => {
          // Progress function
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setProgress(progress);
        },
        (error) => {
          console.error(error);
          toast.error(`Failed to upload video. Please retry or contact support.`, { toastId: 'videoUploadError' });
          setIsUploading(false);
          logEvent(analytics, 'video_upload_error', {error: error.message});
        },
        async () => {
          try {
            // Get the download URL after successful upload
            const url = await getDownloadURL(uploadTask.snapshot.ref);

            // Attempt to add the submission to Firestore
            const submissionsCollectionRef = collection(db, 'submissions');
            const docRef = await addDoc(submissionsCollectionRef, {
              userId: user.uid,
              videoUrl: url,
              timestamp: new Date(),
              goal: goal,
              symptomsText: symptomsText,
              status: 'pending'
            });
            
            toast.success("Video uploaded! Results are on their way.");
            navigate(`/results/${docRef.id}`);
            logEvent(analytics, 'video_upload_success');
          } catch (error) {
            console.error('Add submission error:', error);
            toast.error(`Failed to submit video. Please retry or contact support.`, { toastId: 'submissionError' });
            logEvent(analytics, 'submission_error', { error: error.message });
          } finally {
            setIsUploading(false);
          }
        }
      );
    }
  };

  return (
    <Container>
      <Typography variant="h4" align="center" sx={{ mt: 3, mb: 0.5 }}>
        Upload a video
      </Typography>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
        </Box>
      ) : loadError ? (
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" mt={6} gap={2}>
          <Typography variant="h6">Something went wrong. Please try again.</Typography>
          <Button variant="outlined" onClick={() => window.location.reload()}>Refresh</Button>
        </Box>
      ) : videoPending ? (
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" mt={2}>
          <Typography variant="h6" gutterBottom align="center">
            Your video is currently being processed. You will receive an email when results are ready.
          </Typography>
          <PendingImage altText="processing"/>
        </Box>
      ) : (
        <>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2 }}>
            Follow these tips for best results
          </Typography>
          <Paper elevation={0} variant="outlined" sx={{ overflow: 'hidden', mb: 2 }}>
            <Tabs
              variant="fullWidth"
              value={selectedTab}
              onChange={(event, newValue) => {
                setSelectedTab(newValue);
                logEvent(analytics, 'tutorial_tab_click', { tab_name: newValue });
              }}
              aria-label="Tips"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  minWidth: 'auto',
                  minHeight: 'auto',
                  padding: '10px 12px',
                },
              }}
            >
              <Tab icon={<CameraAltOutlinedIcon />} iconPosition="start" label={<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Angle</Box>} />
              <Tab icon={<SlowMotionVideoOutlinedIcon />} iconPosition="start" label={<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Slow motion</Box>} />
              <Tab icon={<LocationOnOutlinedIcon />} iconPosition="start" label={<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Location</Box>} />
              <Tab icon={<CheckroomOutlinedIcon />} iconPosition="start" label={<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Clothes</Box>} />
              <Tab icon={<TimerOutlinedIcon />} iconPosition="start" label={<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Length</Box>} />
            </Tabs>
            <VerticalTabPanel
              value={selectedTab}
              index={0}
              onStepChange={setSelectedTab}
              text={<>Record <Highlight>side or back view</Highlight> at 90° angle. Capture full body with camera at your foot level.</>}
              videoUrl={videoAngle}
            />
            <VerticalTabPanel
              value={selectedTab}
              index={1}
              onStepChange={setSelectedTab}
              text={<>Use the <Highlight>slow motion</Highlight> mode in your phone's camera app, 120+ frames per second.</>}
              videoUrl={videoFps}
            />
            <VerticalTabPanel
              value={selectedTab}
              index={2}
              onStepChange={setSelectedTab}
              text={<><Highlight>Treadmill or outdoor</Highlight> running is fine. Ensure good lighting so you are clearly visible.</>}
              videoUrl={videoSetting}
            />
            <VerticalTabPanel
              value={selectedTab}
              index={3}
              onStepChange={setSelectedTab}
              text={<>Wear <Highlight>tight clothing</Highlight> like leggings or shorts. Knees and hips must be clearly visible.</>}
              videoUrl={videoClothes}
            />
            <VerticalTabPanel
              value={selectedTab}
              index={4}
              onStepChange={setSelectedTab}
              text={<><Highlight>5-10 seconds of running</Highlight> is sufficient. The slow motion clip will be longer.</>}
              videoUrl={videoLength}
            />
          </Paper>
          <Paper variant="outlined" elevation={0} sx={{ p: 2, mt: 2 }}>
            {isUploading && <LinearProgress variant="determinate" value={progress} />}
            <form onSubmit={handleUpload}>
              {!isUploading && (
                <Stack spacing={2}>
                  <FormControl fullWidth required>
                    <InputLabel id="goal-label">What's your main goal?</InputLabel>
                    <Select
                      labelId="goal-label"
                      id="goal-select"
                      value={goal}
                      label="What's your main goal?"
                      onChange={(e) => setGoal(e.target.value)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="compete">Run competitively</MenuItem>
                      <MenuItem value="health">Stay healthy</MenuItem>
                      <MenuItem value="weight">Lose weight</MenuItem>
                      <MenuItem value="fun">Have fun</MenuItem>
                      <MenuItem value="sports">Play sports</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Any symptoms or areas of concern?"
                    variant="outlined"
                    placeholder="Describe any pain, discomfort, or issues you'd like us to focus on"
                    value={symptomsText}
                    onChange={(event) => setSymptomsText(event.target.value)}
                  />
                  {user ? (
                    <VideoFileInput
                      file={file}
                      onChange={(newFile) => {
                        if (!newFile) { setFile(null); return; }
                        if (newFile.size > 100 * 1024 * 1024) { toast.error('File size must be less than 100 MB.', { toastId: 'videoSizeError' }); return; }
                        if (!newFile.type.startsWith('video/')) { toast.error('File must be a video.', { toastId: 'videoTypeError' }); return; }
                        setFile(newFile);
                      }}
                      disabled={isUploading}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ textTransform: 'none' }}
                      onClick={() => {
                        logEvent(analytics, 'button_click', { button_name: 'prompt_sign_in' });
                        navigate('/signin', { state: { from: '/upload' } });
                      }}>
                      Sign in to upload your video
                    </Button>
                  )}
                </Stack>
              )}
            </form>
          </Paper>
        </>
      )}
      </Container>
  );
}

export default UploadPage;
